import { ApiService } from "./api.service";
import { Injectable } from '@angular/core';
import { AdditionalRequirementsModel, ComplexitySection, LaborModel, MachiningModel, ManufacturingProcessModel, 
        MaterialsModel, OtherCostModel, OverheadModel, ProfitSection, DirectQuoteAssistModel, 
        ToolingSection} from "../app/direct-quote-assist/direct-quote-assist.model";
import { Observable, of } from "rxjs";
import { IndexedDBService } from "./indexed.db.service";
import { MetaDataApiResponse } from "../app/direct-quote-assist/direct.rfq.model";
import { environment } from "../environments/environment";
import { BaseService } from "./base.service";

@Injectable({
    providedIn: 'root'
})
export class DirectQuoteAssistService extends BaseService{
    private baseApiUrl: string;
    private extractedPdfJsonPayload: string | null = null;

    constructor(
        private apiService: ApiService,
        private indexedDBService:IndexedDBService
    ) {
        super();
        this.baseApiUrl = environment.APIEndpoint;
    }
    
    getMetadata(rfqId: string, companyId: number): Observable<MetaDataApiResponse> {
        return this.apiService.get<MetaDataApiResponse>(this.dynamoBaseApiUrl, 
            'supplier/GetQADirectRfqAndSupplierDetails?rfqId=' + rfqId + '&companyId=' + companyId);
    }

    setMetadata(value:string){
        this.extractedPdfJsonPayload = value;
    }

    getComplexity(): Observable<ComplexitySection> {
        if (!this.extractedPdfJsonPayload) {
            return of({} as ComplexitySection);
        }
        return this.apiService.post<ComplexitySection>(this.baseApiUrl, 'complexity', { input: this.extractedPdfJsonPayload });
    }

    getTooling(): Observable<ToolingSection> {
        if (!this.extractedPdfJsonPayload) {
            return of({} as ToolingSection);
        }
        return this.apiService.post<ToolingSection>(this.baseApiUrl, 'tooling', { input: this.extractedPdfJsonPayload });
    }

    getMaterials(): Observable<MaterialsModel[]> {
        if (!this.extractedPdfJsonPayload) {
            return of([] as MaterialsModel[]);
        }
        return this.apiService.post<MaterialsModel[]>(this.baseApiUrl, 'materials', { input: this.extractedPdfJsonPayload });    }

    getManufacturingProcess(): Observable<ManufacturingProcessModel[]> {
        if(!this.extractedPdfJsonPayload){
            return of([] as ManufacturingProcessModel[]);
        }
        return this.apiService.post<ManufacturingProcessModel[]>(this.baseApiUrl, 'manufacturing-process', { input: this.extractedPdfJsonPayload });
    }

    getMachining(): Observable<MachiningModel[]> {
        if (!this.extractedPdfJsonPayload) {
            return of([] as MachiningModel[]);
        }
        return this.apiService.post<MachiningModel[]>(this.baseApiUrl, 'machining', { input: this.extractedPdfJsonPayload });
    }

    getLabor(): Observable<LaborModel[]> {
        if (!this.extractedPdfJsonPayload) {
            return of([] as LaborModel[]);
        }
        return this.apiService.post<LaborModel[]>(this.baseApiUrl, 'labor', { input: this.extractedPdfJsonPayload });
    }

    getAdditionalRequirements(): Observable<AdditionalRequirementsModel[]> {
        if (!this.extractedPdfJsonPayload) {
            return of([] as AdditionalRequirementsModel[]);
        }
        return this.apiService.post<AdditionalRequirementsModel[]>(this.baseApiUrl, 'additional', { input: this.extractedPdfJsonPayload });
    }

    getOverhead(): Observable<OverheadModel[]> {
        if (!this.extractedPdfJsonPayload) {
            return of([] as OverheadModel[]);
        }
        return this.apiService.post<OverheadModel[]>(this.baseApiUrl, 'overhead', { input: this.extractedPdfJsonPayload });
    }

    getProfitMargin(): Observable<ProfitSection> {
        if (!this.extractedPdfJsonPayload) {
            return of({} as ProfitSection);
        }
        return this.apiService.post<ProfitSection>(this.baseApiUrl, 'profit', { input: this.extractedPdfJsonPayload });
    }

    getOtherCosts(): Observable<OtherCostModel[]> {
        if (!this.extractedPdfJsonPayload) {
            return of({} as OtherCostModel[]);
        }
        return this.apiService.post<OtherCostModel[]>(this.baseApiUrl, 'other-costs', { input: this.extractedPdfJsonPayload });
    }

    getAllDataFromLLM(): Observable<DirectQuoteAssistModel>{
        if (!this.extractedPdfJsonPayload) {
            return of({} as DirectQuoteAssistModel);
        }
        return this.apiService.post<DirectQuoteAssistModel>(this.baseApiUrl, 'single-response', { input: this.extractedPdfJsonPayload});
    }
    
    storeQuoteAssistData(quoteAssistModel: any):Observable<number> {
        const data = JSON.parse(JSON.stringify(quoteAssistModel));
        return new Observable(observable => {
        this.indexedDBService.addData(data).subscribe({
            next: (id) => {
                observable.next(id);
            },
            error: (err) => {observable.error(err)},
            complete: () => {},
        })
        });
    }
    getSavedDataFromIndexedDB(rfqId: any):Observable<any> {
        return new Observable(observable=>{
           this.indexedDBService.getDataById(rfqId).subscribe({
                next: (id) => {
                    observable.next(id);
                },
                error: (err) => {observable.error(err)},
                complete: () => {},
              });
        });
    }
    saveDraft(quoteAssistModel: any):Observable<number> {
        const draftData = JSON.parse(JSON.stringify(quoteAssistModel));
        return new Observable<number>(observer => {
            try {
                this.apiService.post<DirectQuoteAssistModel>(
                    this.dynamoBaseApiUrl,
                    'supplier/SaveQASupplierDirectRfqQuoteData',
                    draftData
                ).subscribe({
                    next: (response) => {
                        observer.next(response as unknown as number);
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(err);
                    }
                });
            } catch (error) {
                observer.error(error);
            }
        });
    }
    fetchDraft(companyId: number, rfqId: string): Observable<any>{
        return new Observable(observable=>{
            this.apiService.get<any>(this.dynamoBaseApiUrl, 'supplier/GetQASupplierDirectRfqQuoteData?rfqId=' + rfqId + '&companyId=' + companyId)
            .subscribe({
                 next: (id) => {
                     observable.next(id);
                 },
                 error: (err) => {observable.error(err)},
                 complete: () => {},
               });
         });
    }
    
    saveCompanyMetaData(companyMetadata: any):Observable<number> {
        return new Observable<number>(observer => {
            try {
                this.apiService.post<DirectQuoteAssistModel>(
                    this.dynamoBaseApiUrl,
                    'supplier/SaveQASupplierDetails',
                    companyMetadata
                ).subscribe({
                    next: (response) => {
                        observer.next(response as unknown as number);
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(err);
                    }
                });
            } catch (error) {
                observer.error(error);
            }
        });
    }

    saveTrackingData(trackingData: any):Observable<number> {
        return new Observable<number>(observer => {
            try {
                this.apiService.post<DirectQuoteAssistModel>(
                    this.dynamoBaseApiUrl,
                    'supplier/SaveQASupplierRfqQuoteTracking',
                    trackingData
                ).subscribe({
                    next: (response) => {
                        observer.next(response as unknown as number);
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(err);
                    }
                });
            } catch (error) {
                observer.error(error);
            }
        });
    }

    saveDirectRFQDetails(payload: any):Observable<number> {
        return new Observable<number>(observer => {
            try {
                this.apiService.post<any>(
                    this.dynamoBaseApiUrl,
                    'RFQ/SaveDirectRFQDetails',
                    payload
                ).subscribe({
                    next: (response) => {
                        observer.next(response as unknown as number);
                        observer.complete();
                    },
                    error: (err) => {
                        observer.error(err);
                    }
                });
            } catch (error) {
                observer.error(error);
            }
        });
    }

    getBuyerDetails(rfqId: string, companyId: number): Observable<any>{
        return new Observable(observable=>{
            this.apiService.get<any>(this.dynamoBaseApiUrl, 'supplier/GetQADirectRfqAndSupplierDetails?rfqId=' + rfqId + '&companyId=' + companyId)
            .subscribe({
                 next: (id) => {
                     observable.next(id);
                 },
                 error: (err) => {observable.error(err)},
                 complete: () => {},
               });
         });
    }
    
    getPdfData(rfqId: string): Observable<any> {
        return new Observable(observable => {
            this.indexedDBService.getDataById(rfqId).subscribe({
                next: (data) => {
                    if (data && data.pdfData) {
                        observable.next(data.pdfData);
                    } else {
                        observable.error('No PDF data found');
                    }
                    observable.complete();
                },
                error: (err) => {
                    observable.error('No PDF data found');
                }
            });
        });
    }
}